// The teams taking part in a trade, in the order they were added.
//
// Both builders route players around this list rather than storing a "my side"
// and a "their side": a destination defaults to the next team along, which
// makes an ordinary two-team swap and a three-way rotation the same gesture. At
// two participants `next` and `previous` are both "the other one", so this is
// exactly the behaviour the pages had before multi-team trades existed.

export function nextParticipant(ids, id) {
  const index = ids.indexOf(id)
  if (index === -1) return ids.find((other) => other !== id) ?? null

  return ids[(index + 1) % ids.length] ?? null
}

export function previousParticipant(ids, id) {
  const index = ids.indexOf(id)
  if (index === -1) return ids.find((other) => other !== id) ?? null

  return ids[(index - 1 + ids.length) % ids.length] ?? null
}

// The teams a player held by `fromId` could be sent to.
export function destinationsFor(ids, fromId) {
  return ids.filter((id) => id !== fromId)
}

// Teams not yet in the trade, for the "add a team" picker.
export function availableTeams(teams, participantIds) {
  return teams.filter((team) => !participantIds.includes(team.id))
}

// Drops a team from the trade and re-points anything that referenced it. A
// selection whose source is gone goes with it; one merely *aimed* at the
// departing team is kept and re-aimed, so removing a team never silently
// discards players the user picked from a team that is still in the deal.
export function withoutParticipant(participantIds, selection, teamId) {
  const remaining = participantIds.filter((id) => id !== teamId)
  const next = new Map()

  selection.forEach((entry, playerId) => {
    if (entry.fromTeamId === teamId) return

    const toTeamId = entry.toTeamId === teamId
      ? nextParticipant(remaining, entry.fromTeamId)
      : entry.toTeamId

    if (toTeamId) next.set(playerId, { ...entry, toTeamId })
  })

  return { participantIds: remaining, selection: next }
}

// How many distinct teams the chosen players actually move between — which is
// what the server derives the trade's participants from, not the picker above.
export function tradingTeamIds(selection) {
  const ids = new Set()
  selection.forEach(({ fromTeamId, toTeamId }) => {
    ids.add(fromTeamId)
    ids.add(toTeamId)
  })

  return [...ids]
}

// Participants the user added but never routed a player to or from. They are
// not part of the trade the server will build, so the pages say so rather than
// letting someone believe a team is involved when it is not.
export function idleParticipants(participantIds, selection) {
  const active = new Set(tradingTeamIds(selection))
  return participantIds.filter((id) => !active.has(id))
}
